import React, { useContext, useState, useEffect, useCallback, useMemo } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Alert, Form } from "react-bootstrap";
import axios from "axios";
import { FloatCardComponent } from "../../components/cards";
import MplacerProductsTableComponent from "../../components/tables/Mplacer/MplacerProductsTableComponent";
import LabelFieldComponent from "../../components/fields/LabelFieldComponent";
import PageLayout from "../../layouts/PageLayout";
import { SidebarContext } from "../../context/Sidebar";

const VALID_TABS = ["new", "marketplaced", "sold", "pending"];
const ITEMS_PER_PAGE = 20;
const shrinkableStyle = { minWidth: 0 };

const getAuthHeader = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    Accept: "application/json",
  },
});

const extractData = (res) => {
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.data)) return res.data;
  return [];
};

const getMpStatus = (mp) =>
  mp?.status || (mp?.showing === false || mp?.showing === 0 ? "Sold" : "Unsold");

const TAB_ENDPOINTS = {
  new: "/market-placer/products",
  marketplaced: "/market-placer/market-placed/products",
  sold: "/market-placer/sold-products",
  pending: "/market-placer/pending-sold-products",
};

export default function UserProductList() {
  const { t, n } = useContext(TranslatorContext);
  const { setSidebar } = useContext(SidebarContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("new");
  const [allProducts, setAllProducts] = useState([]);

  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const [counts, setCounts] = useState({
    new: 0,
    marketplaced: 0,
    sold: 0,
    pending: 0,
  });

  const tableHeaders = [
    "Product Code",
    "Title",
    "Selling Price $",
    ...(activeTab === "sold" ? ["Delivery"] : []),
    ...(activeTab !== "new" ? ["Marketplaces"] : []),
    "Action",
  ];

  useEffect(() => {
    const tabParam = new URLSearchParams(location.search).get("tab");
    if (tabParam && VALID_TABS.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  useEffect(() => {
    if (activeTab === "sold") {
      setSidebar(true);
    }
  }, [activeTab, setSidebar]);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setSelectedStatus("");
    setCurrentPage(1);
    navigate(`/mplacer-products?tab=${tabKey}`);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/market-placer/initial-data`,
          getAuthHeader()
        );
        const resourceData = response.data?.data || {};

        if (Array.isArray(resourceData.product_categories)) {
          setCategories(resourceData.product_categories);
        }
        if (Array.isArray(resourceData.brands)) {
          setBrands(resourceData.brands);
        }
      } catch (err) {
        console.error("Error fetching initial dropdown data:", err);
      }
    };

    fetchInitialData();
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const [newRes, marketplacedRes, soldRes, pendingRes] = await Promise.all(
        VALID_TABS.map((tab) => axios.get(`${baseUrl}${TAB_ENDPOINTS[tab]}`, getAuthHeader()))
      );

      setCounts({
        new: extractData(newRes).length,
        marketplaced: extractData(marketplacedRes).length,
        sold: extractData(soldRes).length,
        pending: extractData(pendingRes).length,
      });
    } catch (err) {
      console.error("Error fetching market-placer counts:", err);
    }
  }, []);

  const fetchTabData = useCallback(async (tab) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${TAB_ENDPOINTS[tab]}`,
        getAuthHeader()
      );
      setAllProducts(extractData(response));
    } catch (err) {
      console.error("Error fetching tab data:", err);
      setError(err.response?.data?.message || "Failed to fetch products. Please try again.");
      setAllProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    fetchTabData(activeTab);
  }, [activeTab, fetchTabData]);

  const refreshProducts = useCallback(
    () => Promise.all([fetchTabData(activeTab), fetchCounts()]),
    [activeTab, fetchCounts, fetchTabData]
  );

  const availableStatuses = useMemo(() => {
    const statuses = new Set();
    allProducts.forEach((product) => {
      if (Array.isArray(product.marketPlace)) {
        product.marketPlace.forEach((mp) => {
          const status = getMpStatus(mp);
          if (status) statuses.add(status);
        });
      }
    });
    return Array.from(statuses);
  }, [allProducts]);

  const filteredProducts = useMemo(() => {
    const keyword = search.toLowerCase();

    return allProducts.filter((product) => {
      const matchesSearch =
        !keyword ||
        product.title?.toLowerCase().includes(keyword) ||
        product.product_code?.toLowerCase().includes(keyword);

      const productCatId = product.category?.id || product.category_id;
      const matchesCategory =
        !selectedCategory || String(productCatId) === String(selectedCategory);

      const productBrandId = product.brand?.id || product.brand_id;
      const matchesBrand =
        !selectedBrand || String(productBrandId) === String(selectedBrand);

      let matchesStatus = true;
      if (selectedStatus && Array.isArray(product.marketPlace)) {
        matchesStatus = product.marketPlace.some(
          (mp) => getMpStatus(mp).toLowerCase() === selectedStatus.toLowerCase()
        );
      }

      return matchesSearch && matchesCategory && matchesBrand && matchesStatus;
    });
  }, [allProducts, search, selectedCategory, selectedBrand, selectedStatus]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedCategory, selectedBrand, selectedStatus]);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);

  const cardsData = [
    {
      key: "new",
      title: "New Products",
      digit: counts.new,
      icon: "shopping_bag",
      variant: activeTab === "new" ? "lg blue active-card" : "lg blue",
    },
    {
      key: "marketplaced",
      title: "Marketplaced Products",
      digit: counts.marketplaced,
      icon: "storefront",
      variant: activeTab === "marketplaced" ? "lg purple active-card" : "lg purple",
    },
    {
      key: "sold",
      title: "Sold Products",
      digit: counts.sold,
      icon: "sell",
      variant: activeTab === "sold" ? "lg green active-card" : "lg green",
    },
  ];

  const filterColSize = activeTab !== "new"
    ? { xs: 12, sm: 6, md: 3 }
    : { xs: 12, sm: 6, md: 4 };

  return (
    <PageLayout>
      <Row className="g-3">
        <Col xs={12} style={shrinkableStyle}>
          <div className="mc-card">
            <div className="mc-breadcrumb d-flex flex-wrap align-items-center justify-content-between gap-2">
              <h3 className="mc-breadcrumb-title mb-0">
                {t("product_list")} ({filteredProducts.length})
              </h3>
              <ul className="mc-breadcrumb-list mb-0">
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">{t("home")}</Link>
                </li>
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">{t("products")}</Link>
                </li>
                <li className="mc-breadcrumb-item">{t("product_list")}</li>
              </ul>
            </div>
          </div>
        </Col>

        {cardsData.map((card) => (
          <Col key={card.key} xs={12} sm={4} lg={4}>
            <div style={{ cursor: "pointer" }} onClick={() => handleTabChange(card.key)}>
              <FloatCardComponent
                variant={card.variant}
                digit={n ? n(card.digit) : card.digit}
                title={card.title}
                icon={card.icon}
              />
            </div>
          </Col>
        ))}

        <Col xs={12} className="mt-2 mt-md-4" style={shrinkableStyle}>
          <div className="mc-card p-3 p-md-4" style={shrinkableStyle}>
            {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

            <Row className="g-3 align-items-end">
              <Col {...filterColSize}>
                <Form.Group>
                  <Form.Label className="fw-semibold small text-uppercase text-muted mb-1">
                    Category
                  </Form.Label>
                  <Form.Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    style={{ height: "45px" }}
                    className="w-100"
                  >
                    <option value="">All Categories</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col {...filterColSize}>
                <Form.Group>
                  <Form.Label className="fw-semibold small text-uppercase text-muted mb-1">
                    Brand
                  </Form.Label>
                  <Form.Select
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    style={{ height: "45px" }}
                    className="w-100"
                  >
                    <option value="">All Brands</option>
                    {brands.map((brand) => (
                      <option key={brand.id} value={brand.id}>{brand.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {activeTab !== "new" && (
                <Col {...filterColSize}>
                  <Form.Group>
                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-1">
                      Status
                    </Form.Label>
                    <Form.Select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      style={{ height: "45px" }}
                      className="w-100"
                    >
                      <option value="">All Statuses</option>
                      {availableStatuses.map((st) => (
                        <option key={st} value={st}>{st}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}

              <Col {...filterColSize}>
                <LabelFieldComponent
                  type="search"
                  placeholder="Search by Code or Title..."
                  label="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Col>

              <Col xs={12} className="mt-4" style={shrinkableStyle}>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("loading_products...")}</p>
                  </div>
                ) : (
                  <MplacerProductsTableComponent
                    thead={tableHeaders}
                    tbody={paginatedProducts}
                    activeTab={activeTab}
                    onRefresh={refreshProducts}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                )}
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </PageLayout>
  );
}